import { __decorate } from 'tslib';
import * as i0 from '@angular/core';
import { booleanAttribute, ElementRef, Component, ChangeDetectionStrategy, ViewEncapsulation, ContentChild, Input, NgModule } from '@angular/core';
import { Subject, fromEvent } from 'rxjs';
import { takeUntil, startWith, filter } from 'rxjs/operators';
import * as i1 from 'ng-zorro-antd/core/config';
import { WithConfig } from 'ng-zorro-antd/core/config';
import * as i3 from 'ng-zorro-antd/icon';
import { NzIconDirective, NzIconModule } from 'ng-zorro-antd/icon';
import * as i2 from '@angular/cdk/bidi';
import { ɵNzTransitionPatchModule as _NzTransitionPatchModule } from 'ng-zorro-antd/core/transition-patch';
import { NzWaveModule } from 'ng-zorro-antd/core/wave';
const _c0 = ["nz-button", ""];
const _c1 = ["*"];
function NzButtonComponent_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "span", 0);
  }
}
const NZ_CONFIG_MODULE_NAME = 'button';
let NzButtonComponent = /*#__PURE__*/(() => {
  class NzButtonComponent {
    insertSpan(nodes, renderer) {
      nodes.forEach(node => {
        if (node.nodeName === '#text') {
          const span = renderer.createElement('span');
          const parent = renderer.parentNode(node);
          renderer.insertBefore(parent, span, node);
          renderer.appendChild(span, node);
        }
      });
    }
    get iconOnly() {
      const listOfNode = Array.from(this.elementRef?.nativeElement?.childNodes || []);
      const noText = listOfNode.every(node => node.nodeName !== '#text');
      // ignore icon and comment
      const noSpan = listOfNode.filter(node => {
        return !(node.nodeName === '#comment' || !!node?.attributes?.getNamedItem('nz-icon'));
      }).length == 0;
      return !!this.nzIconDirectiveElement && noSpan && noText;
    }
    constructor(ngZone, elementRef, cdr, renderer, nzConfigService, directionality) {
      this.ngZone = ngZone;
      this.elementRef = elementRef;
      this.cdr = cdr;
      this.renderer = renderer;
      this.nzConfigService = nzConfigService;
      this.directionality = directionality;
      this._nzModuleName = NZ_CONFIG_MODULE_NAME;
      this.nzBlock = false;
      this.nzGhost = false;
      this.nzSearch = false;
      this.nzLoading = false;
      this.nzDanger = false;
      this.disabled = false;
      this.tabIndex = null;
      this.nzType = null;
      this.nzShape = null;
      this.nzSize = 'default';
      this.dir = 'ltr';
      this.destroy$ = new Subject();
      this.loading$ = new Subject();
      this.nzConfigService.getConfigChangeEventForComponent(NZ_CONFIG_MODULE_NAME).pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.cdr.markForCheck();
      });
    }
    ngOnInit() {
      this.directionality.change?.pipe(takeUntil(this.destroy$)).subscribe(direction => {
        this.dir = direction;
        this.cdr.detectChanges();
      });
      this.dir = this.directionality.value;
      this.ngZone.runOutsideAngular(() => {
        // Caretaker note: this event listener could've been added through `host.click` or `HostListener`.
        // The compiler generates the `ɵɵlistener` instruction which wraps the actual listener internally into the
        // function, which runs `markDirty()` before running the actual listener (the decorated class method).
        // Since we're preventing the default behavior and stopping event propagation this doesn't require Angular to run the change detection.
        fromEvent(this.elementRef.nativeElement, 'click', {
          capture: true
        }).pipe(takeUntil(this.destroy$)).subscribe(event => {
          if (this.disabled && event.target?.tagName === 'A' || this.nzLoading) {
            event.preventDefault();
            event.stopImmediatePropagation();
          }
        });
      });
    }
    ngOnChanges(changes) {
      const {
        nzLoading
      } = changes;
      if (nzLoading) {
        this.loading$.next(this.nzLoading);
      }
    }
    ngAfterViewInit() {
      this.insertSpan(this.elementRef.nativeElement.childNodes, this.renderer);
    }
    ngAfterContentInit() {
      this.loading$.pipe(startWith(this.nzLoading), filter(() => !!this.nzIconDirectiveElement), takeUntil(this.destroy$)).subscribe(loading => {
        const nativeElement = this.nzIconDirectiveElement.nativeElement;
        if (loading) {
          this.renderer.setStyle(nativeElement, 'display', 'none');
        } else {
          this.renderer.removeStyle(nativeElement, 'display');
        }
      });
    }
    ngOnDestroy() {
      this.destroy$.next();
      this.destroy$.complete();
    }
    static {
      this.ɵfac = function NzButtonComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NzButtonComponent)(i0.ɵɵdirectiveInject(i0.NgZone), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(i1.NzConfigService), i0.ɵɵdirectiveInject(i2.Directionality));
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: NzButtonComponent,
        selectors: [["button", "nz-button", ""], ["a", "nz-button", ""]],
        contentQueries: function NzButtonComponent_ContentQueries(rf, ctx, dirIndex) {
          if (rf & 1) {
            i0.ɵɵcontentQuery(dirIndex, NzIconDirective, 5, ElementRef);
          }
          if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.nzIconDirectiveElement = _t.first);
          }
        },
        hostAttrs: [1, "ant-btn"],
        hostVars: 34,
        hostBindings: function NzButtonComponent_HostBindings(rf, ctx) {
          if (rf & 2) {
            i0.ɵɵattribute("tabindex", ctx.disabled ? -1 : ctx.tabIndex === null ? null : ctx.tabIndex)("disabled", ctx.disabled || null);
            i0.ɵɵclassProp("ant-btn-default", ctx.nzType === "default")("ant-btn-primary", ctx.nzType === "primary")("ant-btn-dashed", ctx.nzType === "dashed")("ant-btn-link", ctx.nzType === "link")("ant-btn-text", ctx.nzType === "text")("ant-btn-circle", ctx.nzShape === "circle")("ant-btn-round", ctx.nzShape === "round")("ant-btn-lg", ctx.nzSize === "large")("ant-btn-sm", ctx.nzSize === "small")("ant-btn-dangerous", ctx.nzDanger)("ant-btn-loading", ctx.nzLoading)("ant-btn-background-ghost", ctx.nzGhost)("ant-btn-block", ctx.nzBlock)("ant-input-search-button", ctx.nzSearch)("ant-btn-rtl", ctx.dir === "rtl")("ant-btn-icon-only", ctx.iconOnly);
          }
        },
        inputs: {
          nzBlock: [2, "nzBlock", "nzBlock", booleanAttribute],
          nzGhost: [2, "nzGhost", "nzGhost", booleanAttribute],
          nzSearch: [2, "nzSearch", "nzSearch", booleanAttribute],
          nzLoading: [2, "nzLoading", "nzLoading", booleanAttribute],
          nzDanger: [2, "nzDanger", "nzDanger", booleanAttribute],
          disabled: [2, "disabled", "disabled", booleanAttribute],
          tabIndex: "tabIndex",
          nzType: "nzType",
          nzShape: "nzShape",
          nzSize: "nzSize"
        },
        exportAs: ["nzButton"],
        standalone: true,
        features: [i0.ɵɵInputTransformsFeature, i0.ɵɵNgOnChangesFeature, i0.ɵɵStandaloneFeature],
        attrs: _c0,
        ngContentSelectors: _c1,
        decls: 2,
        vars: 1,
        consts: [["nz-icon", "", "nzType", "loading"]],
        template: function NzButtonComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵprojectionDef();
            i0.ɵɵtemplate(0, NzButtonComponent_Conditional_0_Template, 1, 0, "span", 0);
            i0.ɵɵprojection(1);
          }
          if (rf & 2) {
            i0.ɵɵconditional(ctx.nzLoading ? 0 : -1);
          }
        },
        dependencies: [NzIconModule, i3.NzIconDirective],
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  __decorate([WithConfig()], NzButtonComponent.prototype, "nzSize", void 0);
  return NzButtonComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let NzButtonGroupComponent = /*#__PURE__*/(() => {
  class NzButtonGroupComponent {
    constructor(directionality) {
      this.directionality = directionality;
      this.nzSize = 'default';
      this.dir = 'ltr';
      this.destroy$ = new Subject();
    }
    ngOnInit() {
      this.dir = this.directionality.value;
      this.directionality.change?.pipe(takeUntil(this.destroy$)).subscribe(direction => {
        this.dir = direction;
      });
    }
    ngOnDestroy() {
      this.destroy$.next();
      this.destroy$.complete();
    }
    static {
      this.ɵfac = function NzButtonGroupComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NzButtonGroupComponent)(i0.ɵɵdirectiveInject(i2.Directionality));
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: NzButtonGroupComponent,
        selectors: [["nz-button-group"]],
        hostAttrs: [1, "ant-btn-group"],
        hostVars: 6,
        hostBindings: function NzButtonGroupComponent_HostBindings(rf, ctx) {
          if (rf & 2) {
            i0.ɵɵclassProp("ant-btn-group-lg", ctx.nzSize === "large")("ant-btn-group-sm", ctx.nzSize === "small")("ant-btn-group-rtl", ctx.dir === "rtl");
          }
        },
        inputs: {
          nzSize: "nzSize"
        },
        exportAs: ["nzButtonGroup"],
        standalone: true,
        features: [i0.ɵɵStandaloneFeature],
        ngContentSelectors: _c1,
        decls: 1,
        vars: 0,
        template: function NzButtonGroupComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵprojectionDef();
            i0.ɵɵprojection(0);
          }
        },
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return NzButtonGroupComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
let NzButtonModule = /*#__PURE__*/(() => {
  class NzButtonModule {
    static {
      this.ɵfac = function NzButtonModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NzButtonModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: NzButtonModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        imports: [NzButtonComponent, _NzTransitionPatchModule, NzWaveModule]
      });
    }
  }
  return NzButtonModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */

/**
 * Generated bundle index. Do not edit.
 */

export { NzButtonComponent, NzButtonGroupComponent, NzButtonModule };
